import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { supabase, clearAuthStorage } from '../lib/supabase';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)
  const isSigningOut = useRef(false)
  const sessionValidated = useRef(false)

  // Enhanced session cleanup utility
  const clearSession = () => {
    setUser(null)
    setUserProfile(null)
    setProfileLoading(false)
    sessionValidated.current = false
    // Use centralized storage cleanup
    clearAuthStorage()
  }

  // Check for refresh token errors and handle them
  const handleAuthError = (error) => {
    // If we're currently signing out, suppress refresh token errors as they're expected
    if (isSigningOut?.current && 
        (error?.message?.includes('Invalid Refresh Token') || 
         error?.message?.includes('Refresh Token Not Found') ||
         error?.message?.includes('refresh_token_not_found'))) {
      return true // Error handled, suppress it
    }

    // Handle unexpected refresh token errors
    if (error?.message?.includes('Invalid Refresh Token') || 
        error?.message?.includes('Refresh Token Not Found') ||
        error?.message?.includes('refresh_token_not_found')) {
      console.warn('Session expired - refresh token invalid')
      clearSession()
      return true // Indicates error was handled
    }
    return false // Error not handled
  }

  // Isolated async operations - never called from auth callbacks
  const profileOperations = {
    async load(userId) {
      if (!userId || isSigningOut?.current) return
      setProfileLoading(true)
      try {
        const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single()
        if (!error) {
          setUserProfile(data)
        } else if (handleAuthError(error)) {
          return // Session cleared, stop processing
        }
      } catch (error) {
        if (!handleAuthError(error)) {
          console.error('Profile load error:', error)
        }
      } finally {
        setProfileLoading(false)
      }
    },

    clear() {
      setUserProfile(null)
      setProfileLoading(false)
    }
  }

  // Enhanced session recovery with validation
  const recoverSession = async () => {
    try {
      // First validate stored session structure
      const storedSession = localStorage?.getItem('supabase.auth.token') || 
                           localStorage?.getItem(`sb-${supabase?.supabaseUrl?.split('//')?.[1]?.split('.')?.[0]}-auth-token`);
      
      if (!storedSession) {
        console?.log('No stored session found');
        setLoading(false);
        return;
      }

      // Attempt to parse and validate
      try {
        const parsed = JSON?.parse(storedSession);
        if (!parsed?.refresh_token || !parsed?.access_token) {
          console?.warn('Invalid session structure, clearing...');
          clearSession();
          setLoading(false);
          return;
        }
      } catch (parseError) {
        console?.warn('Corrupted session data, clearing...');
        clearSession();
        setLoading(false);
        return;
      }

      // If validation passed, attempt to get session
      const { data: { session }, error } = await supabase?.auth?.getSession();
      
      if (error) {
        // Check if it's a token error
        if (error?.message?.includes('refresh_token') || 
            error?.message?.includes('Invalid Refresh Token')) {
          console?.warn('Session recovery failed with token error, clearing...');
          clearSession();
        } else {
          handleAuthError(error);
        }
        setLoading(false);
        return;
      }
      
      if (session) {
        setUser(session?.user);
        sessionValidated.current = true;
        profileOperations?.load(session?.user?.id);
      } else {
        // No session but no error - clear storage
        clearSession();
      }
    } catch (error) {
      if (!handleAuthError(error)) {
        console?.error('Session recovery error:', error);
        clearSession();
      }
    } finally {
      setLoading(false);
    }
  }

  // Auth state handlers - PROTECTED from async modification
  const authStateHandlers = {
    // This handler MUST remain synchronous - Supabase requirement
    onChange: (event, session) => {
      // Handle auth events specifically
      if (event === 'SIGNED_OUT' || (event === 'TOKEN_REFRESHED' && !session)) {
        clearSession()
        setLoading(false)
        // Reset signout flag after a brief delay
        setTimeout(() => {
          isSigningOut.current = false
        }, 1000)
        return
      }

      setUser(session?.user ?? null)
      setLoading(false)
      
      if (session?.user && !isSigningOut?.current) {
        profileOperations?.load(session?.user?.id) // Fire-and-forget
      } else {
        profileOperations?.clear()
      }
    }
  }

  useEffect(() => {
    // Enhanced session recovery with validation
    recoverSession()

    // CRITICAL: This must remain synchronous
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      (event, session) => {
        try {
          authStateHandlers?.onChange(event, session)
        } catch (error) {
          if (!handleAuthError(error)) {
            console.error('Auth state change error:', error)
          }
        }
      }
    )

    return () => subscription?.unsubscribe()
  }, [])

  // Enhanced auth methods with proactive token validation

  const signIn = async (email, password) => {
    try {
      isSigningOut.current = false
      sessionValidated.current = false
      
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password })
      
      if (error && handleAuthError(error)) {
        return { data: null, error: { message: 'Session expired. Please try signing in again.' } }
      }
      
      if (data?.session) {
        sessionValidated.current = true
      }
      
      return { data, error }
    } catch (error) {
      if (handleAuthError(error)) {
        return { data: null, error: { message: 'Session expired. Please try signing in again.' } }
      }
      console?.error('Sign in error:', error)
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signOut = async () => {
    try {
      // Set flag and clear immediately to prevent race conditions
      isSigningOut.current = true
      sessionValidated.current = false
      
      // Clear local state first
      clearSession()
      
      // Then attempt server-side signout (fire-and-forget)
      supabase?.auth?.signOut()?.catch(error => {
        // Ignore all errors during signout
        console?.warn('Signout API call failed (expected):', error?.message)
      })
      
      // Reset flag after brief delay
      setTimeout(() => {
        isSigningOut.current = false
      }, 1000)
      
      return { error: null }
    } catch (error) {
      // Always clear session and suppress errors
      clearSession()
      
      setTimeout(() => {
        isSigningOut.current = false
      }, 1000)
      
      return { error: null }
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }
    
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single()
      
      if (error && handleAuthError(error)) {
        return { error: { message: 'Session expired. Please sign in again.' } }
      }
      
      if (!error) setUserProfile(data)
      return { data, error }
    } catch (error) {
      if (handleAuthError(error)) {
        return { error: { message: 'Session expired. Please sign in again.' } }
      }
      console.error('Profile update error:', error)
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  // Enhanced refresh session with validation
  const refreshSession = async () => {
    try {
      // Validate before attempting refresh
      const storedSession = localStorage?.getItem('supabase.auth.token') || 
                           localStorage?.getItem(`sb-${supabase?.supabaseUrl?.split('//')?.[1]?.split('.')?.[0]}-auth-token`);
      
      if (!storedSession) {
        clearSession()
        return { success: false, error: 'No session to refresh' }
      }

      const { data, error } = await supabase?.auth?.refreshSession()
      
      if (error) {
        if (handleAuthError(error)) {
          return { success: false, error: 'Session could not be refreshed' }
        }
      }
      
      if (data?.session) {
        sessionValidated.current = true
        return { success: true, data }
      }
      
      // Refresh succeeded but no session - clear
      clearSession()
      return { success: false, error: 'Refresh returned no session' }
    } catch (error) {
      if (!handleAuthError(error)) {
        console?.error('Refresh session error:', error)
      }
      return { success: false, error: 'Session refresh failed' }
    }
  }

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signOut,
    updateProfile,
    refreshSession,
    clearSession,
    isAuthenticated: !!user && sessionValidated?.current
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}